import { useState } from 'react';
import { Plus, Target, Trash2, X } from 'lucide-react';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { useToast } from '../../components/Toast';
import {
  BUCKET_LABELS,
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  useDeleteFocusTask,
  useFocusTasks,
  useMoveFocusTask,
  useUpsertFocusTask,
  type Bucket,
  type FocusTask,
  type FocusTaskInput,
  type Priority,
} from './focusQueries';
import styles from './focus.module.css';

const BUCKETS: Bucket[] = ['today', 'week', 'backlog', 'done'];

export function FocusPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const toast = useToast();

  const { data: tasks = [], isLoading, error } = useFocusTasks(orgId);
  const upsert = useUpsertFocusTask(orgId, userId);
  const move = useMoveFocusTask(orgId);
  const del = useDeleteFocusTask(orgId);

  const [editing, setEditing] = useState<FocusTask | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [quickAdd, setQuickAdd] = useState<Record<Bucket, string>>({
    today: '', week: '', backlog: '', done: '',
  });

  const tasksByBucket: Record<Bucket, FocusTask[]> = {
    today: tasks.filter((t) => t.bucket === 'today'),
    week: tasks.filter((t) => t.bucket === 'week'),
    backlog: tasks.filter((t) => t.bucket === 'backlog'),
    done: tasks.filter((t) => t.bucket === 'done'),
  };

  async function handleQuickAdd(bucket: Bucket) {
    const title = quickAdd[bucket].trim();
    if (!title) return;
    try {
      await upsert.mutateAsync({
        id: null,
        values: { title, description: null, bucket, priority: 'medium', due_at: null },
      });
      setQuickAdd((cur) => ({ ...cur, [bucket]: '' }));
    } catch {
      toast.error('לא הצלחנו להוסיף · ייתכן שטבלת focus_tasks לא נוצרה עדיין (migration 0011)');
    }
  }

  async function handleMove(task: FocusTask, target: Bucket) {
    if (task.bucket === target) return;
    try {
      await move.mutateAsync({ id: task.id, bucket: target });
    } catch {
      toast.error('לא הצלחנו להעביר');
    }
  }

  async function handleDelete(task: FocusTask) {
    if (!window.confirm(`למחוק "${task.title}"?`)) return;
    try {
      await del.mutateAsync(task.id);
      toast.success('המשימה נמחקה');
    } catch {
      toast.error('לא הצלחנו למחוק');
    }
  }

  function openEdit(task: FocusTask) {
    setEditing(task);
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditing(null);
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1>פוקוס · משימות פנימיות</h1>
          <p className={styles.sub}>משימות התפעוליות של פוקוס מוד עצמו (לא של לקוחות). לקוחות יושבים ב-/tasks ובתוך הפרויקטים.</p>
        </div>
        <Target size={32} className={styles.headIcon} aria-hidden="true" />
      </header>

      {error ? (
        <div className={styles.banner}>
          <strong>שגיאה בטעינת משימות.</strong>
          <p>ייתכן שטבלת <code>focus_tasks</code> לא נוצרה עדיין בפרוד. הריצי <code dir="ltr">npx supabase db push --include-all</code> מהמכונה שלך.</p>
        </div>
      ) : null}

      <div className={styles.board}>
        {BUCKETS.map((bucket) => (
          <div key={bucket} className={styles.column}>
            <header className={styles.colHead}>
              <h3>{BUCKET_LABELS[bucket]}</h3>
              <span className={styles.count}>{String(tasksByBucket[bucket].length)}</span>
            </header>

            <input
              type="text"
              className={styles.quickAdd}
              placeholder="+ משימה"
              value={quickAdd[bucket]}
              onChange={(e) => { setQuickAdd((cur) => ({ ...cur, [bucket]: e.target.value })); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleQuickAdd(bucket);
                }
              }}
            />

            <div className={styles.cards}>
              {isLoading ? (
                <p className={styles.notice}>טוען...</p>
              ) : tasksByBucket[bucket].length === 0 ? (
                <p className={styles.empty}>{bucket === 'done' ? 'עוד אין משימות שבוצעו' : 'ריק'}</p>
              ) : (
                tasksByBucket[bucket].map((task) => (
                  <div key={task.id} className={styles.card} onClick={() => { openEdit(task); }} role="button" tabIndex={0}>
                    <div className={styles.cardHead}>
                      <span
                        className={styles.priorityDot}
                        style={{ background: PRIORITY_COLORS[task.priority] }}
                        title={PRIORITY_LABELS[task.priority]}
                      />
                      <strong className={styles.cardTitle}>{task.title}</strong>
                      <button
                        type="button"
                        className={styles.btnGhost}
                        onClick={(e) => { e.stopPropagation(); void handleDelete(task); }}
                        aria-label="מחק"
                      >
                        <Trash2 size={12} aria-hidden="true" />
                      </button>
                    </div>
                    {task.description ? <p className={styles.cardDesc}>{task.description}</p> : null}
                    {bucket !== 'done' ? (
                      <div className={styles.cardActions}>
                        {BUCKETS.filter((b) => b !== bucket).map((target) => (
                          <button
                            key={target}
                            type="button"
                            className={styles.btnMove}
                            onClick={(e) => { e.stopPropagation(); void handleMove(task, target); }}
                          >
                            → {BUCKET_LABELS[target]}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.cardActions}>
                        <button
                          type="button"
                          className={styles.btnMove}
                          onClick={(e) => { e.stopPropagation(); void handleMove(task, 'backlog'); }}
                        >
                          ↩ החזר ל-בהמתנה
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      <button type="button" className={styles.fab} onClick={() => { setEditing(null); setDrawerOpen(true); }}>
        <Plus size={20} aria-hidden="true" />
      </button>

      {drawerOpen ? (
        <FocusDrawer
          task={editing}
          onClose={closeDrawer}
          onSave={async (values) => {
            try {
              await upsert.mutateAsync({ id: editing?.id ?? null, values });
              toast.success(editing ? 'המשימה עודכנה' : 'המשימה נוצרה');
              closeDrawer();
            } catch {
              toast.error('לא הצלחנו לשמור · ייתכן שטבלת focus_tasks לא קיימת עדיין');
            }
          }}
        />
      ) : null}
    </div>
  );
}

function FocusDrawer({ task, onClose, onSave }: { task: FocusTask | null; onClose: () => void; onSave: (v: FocusTaskInput) => Promise<void> }) {
  const [form, setForm] = useState<FocusTaskInput>({
    title: task?.title ?? '',
    description: task?.description ?? null,
    bucket: task?.bucket ?? 'backlog',
    priority: task?.priority ?? 'medium',
    due_at: task?.due_at ?? null,
  });

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
      onClick={onClose}
    >
      <div className={styles.drawer} onClick={(e) => { e.stopPropagation(); }}>
        <div className={styles.drawerHead}>
          <h3>{task ? 'עריכת משימה' : 'משימה חדשה'}</h3>
          <button type="button" className={styles.btnGhost} onClick={onClose} aria-label="סגור">
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className={styles.field}>
          <label htmlFor="ft-title">כותרת</label>
          <input
            id="ft-title"
            type="text"
            className={styles.input}
            value={form.title}
            placeholder="לדוגמה: לחדש דומיין · לכתוב פוסט · להגדיר 2FA"
            onChange={(e) => { setForm((cur) => ({ ...cur, title: e.target.value })); }}
            autoFocus
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="ft-desc">תיאור</label>
          <textarea
            id="ft-desc"
            className={styles.textarea}
            value={form.description ?? ''}
            onChange={(e) => { setForm((cur) => ({ ...cur, description: e.target.value })); }}
          />
        </div>

        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="ft-bucket">דלי</label>
            <select
              id="ft-bucket"
              className={styles.select}
              value={form.bucket}
              onChange={(e) => { setForm((cur) => ({ ...cur, bucket: e.target.value as Bucket })); }}
            >
              <option value="today">היום</option>
              <option value="week">השבוע</option>
              <option value="backlog">בהמתנה</option>
              <option value="done">בוצע</option>
            </select>
          </div>
          <div className={styles.field}>
            <label htmlFor="ft-priority">דחיפות</label>
            <select
              id="ft-priority"
              className={styles.select}
              value={form.priority}
              onChange={(e) => { setForm((cur) => ({ ...cur, priority: e.target.value as Priority })); }}
            >
              <option value="low">נמוכה</option>
              <option value="medium">בינונית</option>
              <option value="high">גבוהה</option>
              <option value="urgent">דחופה</option>
            </select>
          </div>
        </div>

        <div className={styles.field}>
          <label htmlFor="ft-due">תאריך יעד</label>
          <input
            id="ft-due"
            type="date"
            className={styles.input}
            value={form.due_at ? form.due_at.slice(0, 10) : ''}
            onChange={(e) => { setForm((cur) => ({ ...cur, due_at: e.target.value || null })); }}
          />
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.btnSecondary} onClick={onClose}>בטל</button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => { if (form.title.trim()) void onSave(form); }}
            disabled={!form.title.trim()}
          >
            שמור
          </button>
        </div>
      </div>
    </div>
  );
}
